// src/models/Insights/versionCurrency.ts
//
// Pure computation of the three-tier up-to-date/needs-update/unsupported split for the
// Insights mobile card, per platform. Firmware only gets a two-tier split
// (`computeFirmwareCurrency`, below) because `FirmwareLookupTableOnAzure` has no
// `minimum_firmware_version` - mobile has both a `latest_app_version_*` and a
// `minimum_app_version_*`, so a third "unsupported" tier below the minimum is computable for
// mobile but not for firmware. This is the feature's Rule 8 exception: a direct
// equality/ordering comparison against known-good "latest"/"minimum" values is allowed, but
// an inferred release-age ranking (e.g. "N versions behind") is not, because there is no
// ordered release history to compute that from - only single latest/minimum strings per
// platform.

import { isVersionOutdated } from '@/utils/versionCompare';
import { UNKNOWN_VERSION_LABEL, type VersionBucket } from '@/models/Insights/InsightsTypes';

/**
 * A Compass parked in the Nordic bootloader after a failed/interrupted DFU reports firmware
 * version "0.0.0.0" permanently - it never self-heals on its own (see the comment at
 * ios-app/Safeguard/Models/Firmware Management/CompassFirmwareManager.swift ~line 216-221).
 * These devices need manual recovery, so they must always be surfaced as their own visible
 * signal rather than folded into "needs update" (that undersells the urgency) or silently
 * dropped as junk data. Moved here (formerly in `VersionDistributionChart.tsx`, which this
 * file's `computeFirmwareCurrency` replaces) since this is now the one place that classifies
 * firmware buckets.
 */
export const BOOTLOADER_FIRMWARE_VERSION = '0.0.0.0';

export type VersionCurrencyTier = 'upToDate' | 'needsUpdate' | 'unsupported';

export type PlatformCurrency = {
  platform: string;
  total: number;
  upToDate: number;
  needsUpdate: number;
  unsupported: number;
};

/**
 * Splits `buckets` into upToDate/needsUpdate/unsupported totals per platform, one
 * `PlatformCurrency` per non-empty `latestByPlatform` entry - a platform whose latest version
 * is null/empty is omitted entirely (the caller renders nothing for it) rather than emitting a
 * zeroed row, since there is nothing to compare against at all. Only buckets whose `.group`
 * matches the platform key are included (mobile buckets' `group` is already the platform
 * label, e.g. "iOS"/"Android").
 *
 * Tier rules, checked in this order:
 *   1. `UNKNOWN_VERSION_LABEL` buckets are excluded from `total` and all three tiers -
 *      `isVersionOutdated` returns `false` for empty/unparseable input, so folding Unknown into
 *      upToDate would silently inflate the good number.
 *   2. Below `minimumVersion` (when known) -> `unsupported`.
 *   3. At/above `minimumVersion` but below `latestVersion` -> `needsUpdate`.
 *   4. At or above `latestVersion` -> `upToDate` (a newer-than-latest beta counts as up to
 *      date, not as some fourth "ahead" state).
 * When `minimumVersion` is null/empty but `latestVersion` is known, only rules 3/4 apply -
 * `unsupported` stays 0 for that platform rather than misclassifying real devices.
 */
export function computePlatformCurrency(
  buckets: VersionBucket[],
  latestByPlatform: Map<string, string | null>,
  minimumByPlatform: Map<string, string | null>,
): PlatformCurrency[] {
  const results: PlatformCurrency[] = [];

  for (const [platform, latestVersion] of latestByPlatform) {
    if (!latestVersion) continue;

    const minimumVersion = minimumByPlatform.get(platform) ?? null;
    const platformBuckets = buckets.filter((bucket) => bucket.group === platform);

    let total = 0;
    let upToDate = 0;
    let needsUpdate = 0;
    let unsupported = 0;

    for (const bucket of platformBuckets) {
      if (bucket.version === UNKNOWN_VERSION_LABEL) continue;

      total += bucket.count;

      const belowMinimum = minimumVersion
        ? isVersionOutdated(bucket.version, minimumVersion)
        : false;
      const belowLatest = isVersionOutdated(bucket.version, latestVersion);

      if (belowMinimum) {
        unsupported += bucket.count;
      } else if (belowLatest) {
        needsUpdate += bucket.count;
      } else {
        upToDate += bucket.count;
      }
    }

    results.push({ platform, total, upToDate, needsUpdate, unsupported });
  }

  return results;
}

export type FirmwareCurrency = {
  total: number;
  upToDate: number;
  needsUpdate: number;
  /** Devices whose latest `c_fw` was null/empty (`UNKNOWN_VERSION_LABEL`) - excluded from
   * `total`/every tier, computed regardless of whether `latestVersion` is known, since it is
   * not a currency judgement at all. */
  unknownCount: number;
  /** Devices parked at `BOOTLOADER_FIRMWARE_VERSION` - excluded from `total`/every tier for
   * the same reason as `unknownCount`; computed regardless of `latestVersion` so this
   * always-actionable signal never disappears just because the release catalog is
   * loading/failed. */
  bootloaderCount: number;
};

/**
 * Fleet-wide two-tier (upToDate/needsUpdate) split for the firmware card - no `unsupported`
 * tier, since firmware has no minimum-version catalog value (see this file's header
 * comment). Unlike `computePlatformCurrency`, this is not per-group: the firmware card shows
 * one bar for the whole fleet, not one per `is_core` class (Compass Core/Pro), so every
 * bucket is considered regardless of `.group`.
 *
 * Exclusion order, both checked before `isVersionOutdated` and before `latestVersion` is
 * even consulted:
 *   1. `UNKNOWN_VERSION_LABEL` buckets (null/empty `c_fw`) - counted into `unknownCount`,
 *      never `total`/either tier, for the same reason as `computePlatformCurrency`'s rule 1:
 *      `isVersionOutdated` returns `false` for empty input, so folding Unknown into upToDate
 *      would silently inflate the good number.
 *   2. `BOOTLOADER_FIRMWARE_VERSION` buckets - counted into `bootloaderCount`, never
 *      `total`/either tier. Not a real, evaluable version any more than Unknown is, so it
 *      gets the same treatment, but the mobile card has no equivalent sentinel to mirror -
 *      this exclusion is new to firmware.
 *   3. Everything else: `isVersionOutdated(version, latestVersion) ? needsUpdate : upToDate`
 *      (at or above latest counts as up to date, matching `computePlatformCurrency`'s rule).
 *
 * `unknownCount`/`bootloaderCount` are computed unconditionally, but `total`/`upToDate`/
 * `needsUpdate` all stay 0 when `latestVersion` is null/empty - mirroring
 * `computePlatformCurrency`'s "nothing to compare against, so no numbers, not a misleading
 * zero-implies-perfect-score" convention for the currency tiers specifically. The bootloader
 * count in particular must stay visible even then (see `FirmwareCurrency.bootloaderCount`).
 */
export function computeFirmwareCurrency(
  buckets: VersionBucket[],
  latestVersion: string | null,
): FirmwareCurrency {
  let total = 0;
  let upToDate = 0;
  let needsUpdate = 0;
  let unknownCount = 0;
  let bootloaderCount = 0;

  for (const bucket of buckets) {
    if (bucket.version === UNKNOWN_VERSION_LABEL) {
      unknownCount += bucket.count;
      continue;
    }
    if (bucket.version === BOOTLOADER_FIRMWARE_VERSION) {
      bootloaderCount += bucket.count;
      continue;
    }
    if (!latestVersion) continue;

    total += bucket.count;
    if (isVersionOutdated(bucket.version, latestVersion)) {
      needsUpdate += bucket.count;
    } else {
      upToDate += bucket.count;
    }
  }

  return { total, upToDate, needsUpdate, unknownCount, bootloaderCount };
}
